import React, { useState } from 'react';
import { X, Loader2, Wand2, Copy, FileText, Download, ExternalLink } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';

interface SavedOutput {
  id: string;
  saved: boolean;
}

interface GanttChartModalProps {
  onClose: () => void;
}

export default function GanttChartModal({ onClose }: GanttChartModalProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [planGenerated, setPlanGenerated] = useState(false);
  const [savedOutput, setSavedOutput] = useState<SavedOutput | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please enter project details');
      return;
    }

    setLoading(true);
    try {
      const prompt = `Create a detailed Gantt chart timeline for the following project:
      ${input}

      Please structure the response in the following format (without HTML tags):

      Project Name: [Project Name]
      Duration: [Total Duration]
      Team: [Team Composition]

      Project Timeline & Phases:
      [Phase 1 Name]
      Duration: [Start - End]
      Activities: [Key Activities]
      [Additional Details]

      [Phase 2 Name]
      Duration: [Start - End]
      Activities: [Key Activities]
      [Additional Details]

      [Continue with all phases...]

      Dependencies:
      [List key dependencies between phases]

      Milestones:
      [List key project milestones with dates]

      Task Name | Start Date | Duration | Dependencies
      [Task 1] | [Start Date] | [Duration] | [Dependencies]
      [Task 2] | [Start Date] | [Duration] | [Dependencies]
      [Continue with all tasks...]

      Rules:
      1. Break down the project into clear, manageable tasks and phases
      2. Specify start dates relative to project start (Day 1, Week 2, etc.)
      3. Specify duration in days or weeks
      4. List dependencies for each task
      5. Ensure logical task sequencing
      6. Include key milestones
      7. Consider resource constraints
      8. Add buffer time for uncertainties
      
      Note: If any information is not provided in the input, please make reasonable assumptions based on industry standards and best practices.`;

      const response = await generateTaskDescription(prompt, 'task');
      
      // Clean up any HTML tags in the response
      const cleanedResponse = response.replace(/<[^>]*>/g, '');
      setResult(cleanedResponse);
      setPlanGenerated(true);
      toast.success('Gantt chart generated successfully');

      // Prepare for saving but don't save automatically
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating Gantt chart:', error);
      toast.error('Failed to generate Gantt chart');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }
      
      // First try to find the agent by ID
      const { data: existingAgent, error: fetchError } = await supabase
        .from('ai_agents')
        .select('id')
        .eq('id', '00000000-0000-0000-0000-000000000003')
        .limit(1)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error fetching agent:', fetchError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      let agentId: string;
      
      if (!existingAgent) {
        // Try to find by name
        const { data: agentByName, error: nameError } = await supabase
          .from('ai_agents')
          .select('id')
          .eq('name', 'Gantt Chart Generator')
          .limit(1)
          .maybeSingle();
          
        if (nameError && nameError.code !== 'PGRST116') {
          console.error('Error finding agent by name:', nameError);
          toast.error('Failed to save output');
          setSavingOutput(false);
          return;
        }
        
        if (agentByName) {
          agentId = agentByName.id;
        } else {
          // Create new agent
          const { data: newAgent, error: createError } = await supabase
            .from('ai_agents')
            .insert({
              name: 'Gantt Chart Generator',
              user_id: user.id,
              goals: 'Create visual project timelines and dependencies',
              system_prompt: 'You are an expert Gantt chart creator. Create detailed project timelines with phases, dependencies, and milestones.',
              is_custom: false,
              agent_type: 'text'
            })
            .select('id')
            .single();
            
          if (createError) {
            console.error('Error creating agent:', createError);
            toast.error('Failed to create agent');
            setSavingOutput(false);
            return;
          }
          
          agentId = newAgent.id;
        }
      } else {
        agentId = existingAgent.id;
      }
      
      // Save output to ai_agent_outputs table
      const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
        user_id: user.id,
        agent_id: agentId,
        prompt: input,
        output_url: result,
        generation_type: 'text',
        llm_model: 'gpt-3.5-turbo',
        metadata: {},
        agent_type: 'text'
      });

      if (insertError) {
        console.error('Error saving output:', insertError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      toast.success('Output saved successfully');
      setSavedOutput({...savedOutput, saved: true});
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result || '');
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    try {
      if (!result) return;
      
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      
      // Add header
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      
      // Extract project name from result
      let projectName = "Gantt Chart";
      const projectNameMatch = result.match(/Project Name:\s*([^\n]+)/);
      if (projectNameMatch && projectNameMatch[1]) {
        projectName = projectNameMatch[1].trim();
      }
      
      const titleWidth = pdf.getStringUnitWidth(projectName) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(projectName, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Start position after header
      let y = 50;
      
      // Add original input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Details:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const wrappedInput = pdf.splitTextToSize(input, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Parse the result into sections
      const sections = parseResultSections(result);
      
      // Add each section
      Object.entries(sections).forEach(([title, content]) => {
        // Check if we need a new page
        if (y > pageHeight - 40) {
          pdf.addPage();
          y = 20;
        }
        
        // Add section title
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setFillColor(240, 247, 255); // Light blue background
        pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
        pdf.text(title, margin, y);
        y += 15;
        
        // Add section content
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        const contentLines = content.split('\n');
        contentLines.forEach(line => {
          // Check if we need a new page
          if (y > pageHeight - 20) {
            pdf.addPage();
            y = 20;
          }
          
          if (line.trim()) {
            // Check if this is a bullet point
            if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
              const bulletText = line.trim().substring(1).trim();
              pdf.text('•', margin, y);
              const wrappedText = pdf.splitTextToSize(bulletText, pageWidth - (margin * 2) - 10);
              pdf.text(wrappedText, margin + 10, y);
              y += wrappedText.length * 7;
            } else {
              const wrappedText = pdf.splitTextToSize(line, pageWidth - (margin * 2));
              pdf.text(wrappedText, margin, y);
              y += wrappedText.length * 7;
            }
          } else {
            y += 3; // Empty line
          }
        });
        
        y += 10; // Add space between sections
      });
      
      // Add footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`, margin, pageHeight - 10);
      }
      
      pdf.save(`${projectName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-gantt-chart.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const parseResultSections = (result: string): Record<string, string> => {
    const sections: Record<string, string> = {};
    
    // Extract project details
    const projectDetailsMatch = result.match(/Project Name:[\s\S]*?(?=Project Timeline|$)/);
    if (projectDetailsMatch) {
      sections['Project Details'] = projectDetailsMatch[0].trim();
    }
    
    // Extract timeline
    const timelineMatch = result.match(/Project Timeline[\s\S]*?(?=Dependencies|$)/);
    if (timelineMatch) {
      sections['Project Timeline'] = timelineMatch[0].trim();
    }
    
    // Extract dependencies
    const dependenciesMatch = result.match(/Dependencies:[\s\S]*?(?=Milestones|$)/);
    if (dependenciesMatch) {
      sections['Dependencies'] = dependenciesMatch[0].trim();
    }
    
    // Extract milestones
    const milestonesMatch = result.match(/Milestones:[\s\S]*?(?=Task Name|$)/);
    if (milestonesMatch) {
      sections['Milestones'] = milestonesMatch[0].trim();
    }
    
    // Extract task table
    const taskTableMatch = result.match(/Task Name[\s\S]*$/);
    if (taskTableMatch) {
      sections['Task Details'] = taskTableMatch[0].trim();
    }
    
    return sections;
  };

  const downloadExamplePDF = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      
      // Add title
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Mobile App for Food Delivery', margin, 25);
      
      // Reset text color
      pdf.setTextColor(0, 0, 0);
      
      let y = 50;
      
      // Add user input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('User Input:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const userInput = "I'm building a mobile app for food delivery. Right now I'm collecting requirements from a few restaurants. After that, I'll work with my designer to make wireframes. Then development will start — planning to use Flutter. I've got one backend dev and one frontend dev. Hoping to launch in 3 months. Also want to leave some time for marketing and bug fixes before launch.";
      const wrappedUserInput = pdf.splitTextToSize(userInput, pageWidth - (margin * 2));
      pdf.text(wrappedUserInput, margin, y);
      y += wrappedUserInput.length * 7 + 15;
      
      // Project details section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Project Details', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const projectDetails = [
        'Project Name: Mobile App for Food Delivery',
        'Duration: 3 months (Approx. 12 weeks)',
        'Team: 1 Backend Developer, 1 Frontend Developer, 1 UI/UX Designer'
      ];
      
      projectDetails.forEach(detail => {
        pdf.text(detail, margin, y);
        y += 7;
      });
      
      y += 10;
      
      // Project Timeline section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Project Timeline & Phases', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const phases = [
        'Requirements Gathering',
        'Duration: Week 1 – Week 2',
        'Activities: Discuss features with restaurants, finalize core functionalities',
        '',
        'UI/UX Design',
        'Duration: Week 3 – Week 4',
        'Activities: Wireframes, user flows, prototype creation',
        'Responsible: UI/UX Designer',
        '',
        'Frontend & Backend Development',
        'Duration: Week 5 – Week 9',
        'Activities:',
        '- Frontend: App screens using Flutter',
        '- Backend: APIs, database setup, order & delivery logic',
        '',
        'Internal Testing & Bug Fixes',
        'Duration: Week 10 – Week 11',
        'Activities: QA testing, performance improvements, bug fixing',
        '',
        'Marketing & Launch Prep',
        'Duration: Week 12',
        'Activities: Create launch plan, build promotional content, app store readiness'
      ];
      
      phases.forEach(phase => {
        // Check if we need a new page
        if (y > pageHeight - 20) {
          pdf.addPage();
          y = 20;
        }
        
        if (phase.trim()) {
          if (phase.trim().startsWith('-')) {
            pdf.text('•', margin + 5, y);
            const bulletText = phase.trim().substring(1).trim();
            const wrappedText = pdf.splitTextToSize(bulletText, pageWidth - (margin * 2) - 15);
            pdf.text(wrappedText, margin + 15, y);
            y += wrappedText.length * 7;
          } else {
            const wrappedText = pdf.splitTextToSize(phase, pageWidth - (margin * 2));
            pdf.text(wrappedText, margin, y);
            y += wrappedText.length * 7;
          }
        } else {
          y += 5; // Empty line
        }
      });
      
      // Add a new page for dependencies and milestones
      pdf.addPage();
      y = 20;
      
      // Dependencies section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Dependencies', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const dependencies = [
        'Development depends on completed wireframes',
        'Testing begins after at least one functional build',
        'Marketing should start once core app functionality is stable'
      ];
      
      dependencies.forEach(dependency => {
        pdf.text('•', margin, y);
        const wrappedDependency = pdf.splitTextToSize(dependency, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedDependency, margin + 10, y);
        y += wrappedDependency.length * 7;
      });
      
      y += 15;
      
      // Milestones section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Milestones', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const milestones = [
        'Wireframe approval by Week 4',
        'First working build by Week 7',
        'Final testing by Week 11',
        'App launch at end of Week 12'
      ];
      
      milestones.forEach(milestone => {
        pdf.text('•', margin, y);
        const wrappedMilestone = pdf.splitTextToSize(milestone, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedMilestone, margin + 10, y);
        y += wrappedMilestone.length * 7;
      });
      
      y += 15;
      
      // Task Details section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Task Details', margin, y);
      y += 15;
      
      // Task table header
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, y - 5, pageWidth - (margin * 2), 10, 'F');
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      
      const col1Width = 60;
      const col2Width = 40;
      const col3Width = 30;
      const col4Width = pageWidth - margin * 2 - col1Width - col2Width - col3Width;
      
      pdf.text('Task Name', margin, y);
      pdf.text('Start Date', margin + col1Width, y);
      pdf.text('Duration', margin + col1Width + col2Width, y);
      pdf.text('Dependencies', margin + col1Width + col2Width + col3Width, y);
      y += 10;
      
      // Task table rows
      pdf.setFont('helvetica', 'normal');
      
      const tasks = [
        { name: 'Requirements Gathering', start: 'Week 1', duration: '2 weeks', dependencies: 'None' },
        { name: 'UI/UX Design', start: 'Week 3', duration: '2 weeks', dependencies: 'Requirements Gathering' },
        { name: 'Frontend Development', start: 'Week 5', duration: '5 weeks', dependencies: 'UI/UX Design' },
        { name: 'Backend Development', start: 'Week 5', duration: '4 weeks', dependencies: 'Requirements Gathering' },
        { name: 'API Integration', start: 'Week 8', duration: '2 weeks', dependencies: 'Backend Development' },
        { name: 'Internal Testing', start: 'Week 10', duration: '2 weeks', dependencies: 'Frontend Dev, API Integration' },
        { name: 'Bug Fixes', start: 'Week 10', duration: '2 weeks', dependencies: 'Testing findings' },
        { name: 'Marketing Preparation', start: 'Week 11', duration: '2 weeks', dependencies: 'Stable app version' },
        { name: 'App Store Submission', start: 'Week 12', duration: '1 week', dependencies: 'Final testing' },
        { name: 'Launch', start: 'Week 12', duration: '1 day', dependencies: 'App Store approval' }
      ];
      
      tasks.forEach((task, index) => {
        // Check if we need a new page
        if (y > pageHeight - 20) {
          pdf.addPage();
          y = 20;
          
          // Repeat header on new page
          pdf.setFillColor(245, 247, 250);
          pdf.rect(margin, y - 5, pageWidth - (margin * 2), 10, 'F');
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Task Name', margin, y);
          pdf.text('Start Date', margin + col1Width, y);
          pdf.text('Duration', margin + col1Width + col2Width, y);
          pdf.text('Dependencies', margin + col1Width + col2Width + col3Width, y);
          y += 10;
          pdf.setFont('helvetica', 'normal');
        }
        
        // Add alternating row background
        if (index % 2 === 0) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(margin, y - 5, pageWidth - (margin * 2), 10, 'F');
        }
        
        pdf.text(task.name, margin, y);
        pdf.text(task.start, margin + col1Width, y);
        pdf.text(task.duration, margin + col1Width + col2Width, y);
        pdf.text(task.dependencies, margin + col1Width + col2Width + col3Width, y);
        y += 10;
      });
      
      // Add footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated by PM Pro AI | Page ${i} of ${pageCount}`, margin, pageHeight - 10);
      }
      
      pdf.save('gantt-chart-example.pdf');
    } catch (error) {
      console.error('Error creating example PDF:', error);
      toast.error('Failed to create example PDF');
    }
  };

  const toggleFullResponse = () => {
    setShowFullResponse(!showFullResponse);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Gantt Chart Generator
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">Instructions</h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
              Describe your project details including scope, timeline, team composition, and key deliverables. The AI will generate a comprehensive Gantt chart with phases, dependencies, and milestones.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={downloadExamplePDF}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Example PDF
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="project-details" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Project Details
              </label>
              <textarea
                id="project-details"
                rows={5}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Describe your project here (e.g., I'm building a mobile app for food delivery with 1 backend dev and 1 frontend dev. Timeline is 3 months...)"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Generating Chart...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Generate Chart
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Project Timeline
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    Copy
                  </button>
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleSaveOutput}
                    disabled={savingOutput || (savedOutput && savedOutput.saved)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingOutput ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-1.5" />
                        Saving...
                      </>
                    ) : savedOutput && savedOutput.saved ? (
                      'Saved'
                    ) : (
                      'Save Output'
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-4">
                <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                  Gantt Chart Generated Successfully! You can now download it as a PDF.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}